import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

export default function CartPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { cart, cartProducts, updateCartItem, removeFromCart, getCartTotal, loading } = useCart();

  const getProduct = (productId) => cartProducts.find(p => p.id === productId);

  const handleUpdateQuantity = async (item, newQuantity) => {
    try {
      await updateCartItem(item.product_id, item.variant_index, newQuantity);
    } catch (error) {
      toast.error('Failed to update quantity');
    }
  };

  const handleRemove = async (item) => {
    try {
      await removeFromCart(item.product_id, item.variant_index);
      toast.success('Item removed from cart');
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const handleCheckout = () => {
    if (!user) {
      toast.error('Please login to checkout');
      return;
    }
    navigate('/checkout');
  };

  const subtotal = getCartTotal();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-12 w-12 rounded-full border-4 border-primary border-t-transparent loading-spinner" />
      </div>
    );
  }

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <ShoppingBag className="h-20 w-20 mx-auto text-muted-foreground/50 mb-6" />
            <h1 className="font-serif text-3xl font-semibold text-stone-900 mb-4">
              Your cart is empty
            </h1>
            <p className="text-muted-foreground mb-8">
              Add some organic products to your cart and they will appear here
            </p>
            <Link to="/products">
              <Button className="bg-primary hover:bg-primary/90 rounded-full px-8 py-6 text-lg">
                Start Shopping <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-serif text-3xl font-semibold text-stone-900 mb-8">Shopping Cart</h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.items.map((item, index) => {
              const product = getProduct(item.product_id);
              if (!product) return null;
              const variant = product.variants[item.variant_index];
              
              return (
                <div key={index} className="bg-white rounded-2xl border border-stone-100 p-4 sm:p-6" data-testid={`cart-item-${product.id}`}>
                  <div className="flex gap-4 sm:gap-6">
                    {/* Image */}
                    <Link to={`/products/${product.id}`} className="flex-shrink-0">
                      <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-xl overflow-hidden bg-stone-50">
                        <img
                          src={product.images?.[0] || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=200'}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </Link>

                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <Link to={`/products/${product.id}`}>
                        <h3 className="font-medium text-stone-900 hover:text-primary transition-colors line-clamp-2">
                          {product.name}
                        </h3>
                      </Link>
                      <p className="text-sm text-muted-foreground mt-1">
                        Size: {variant.size}
                      </p>
                      <p className="text-lg font-semibold text-primary mt-2">
                        ₹{variant.price}
                      </p>

                      <div className="flex items-center justify-between mt-4">
                        {/* Quantity */}
                        <div className="flex items-center border border-stone-200 rounded-full">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleUpdateQuantity(item, item.quantity - 1)}
                            disabled={item.quantity <= 1}
                            className="h-8 w-8 rounded-full"
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="w-10 text-center text-sm font-medium">{item.quantity}</span>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleUpdateQuantity(item, item.quantity + 1)}
                            className="h-8 w-8 rounded-full"
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>

                        {/* Remove */}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemove(item)}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          data-testid={`remove-item-${product.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Subtotal (Desktop) */}
                    <div className="hidden sm:block text-right">
                      <p className="text-lg font-semibold text-stone-900">
                        ₹{variant.price * item.quantity}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl border border-stone-100 p-6 sticky top-32">
              <h2 className="font-semibold text-lg text-stone-900 mb-6">Order Summary</h2>

              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-medium">₹{subtotal}</span>
                </div>
                <p className="text-xs text-muted-foreground italic">
                  * Delivery charges applicable
                </p>
                <div className="border-t border-stone-100 pt-4">
                  <div className="flex justify-between">
                    <span className="font-semibold text-stone-900">Total</span>
                    <span className="font-semibold text-xl text-primary">₹{subtotal}</span>
                  </div>
                </div>
              </div>

              <Button
                onClick={handleCheckout}
                className="w-full mt-6 bg-primary hover:bg-primary/90 rounded-full py-6 text-lg"
                data-testid="proceed-to-checkout"
              >
                Proceed to Checkout
              </Button>

              <p className="text-center text-sm text-muted-foreground mt-4">
                Cash on Delivery available
              </p>

              <Link to="/products" className="block text-center text-primary hover:underline mt-4 text-sm font-medium">
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
